import { z } from "zod";

const id = z
  .string()
  .min(1)
  .max(100)
  .regex(/^[a-zA-Z0-9_-]+$/);
/** Knowing a prediction or promise exists does not make its outcome inevitable. */
export const zStoryFutureIntent = z
  .object({
    basis: z.enum(["vision", "prophecy", "plan", "commitment", "established"]),
    certainty: z.enum(["possible", "fixed"]),
  })
  .strict()
  .refine((value) => value.certainty !== "fixed" || value.basis === "established", {
    message: "Only an explicitly established future event is fixed; visions, plans and promises may not come true.",
  });
export type StoryFutureIntent = z.infer<typeof zStoryFutureIntent>;
export function storyTemporalFrameLabel(frame: { kind: string; future?: StoryFutureIntent }) {
  if (frame.kind === "future")
    return `Future · ${frame.future?.basis || "unspecified"} · ${frame.future?.certainty === "fixed" ? "will happen" : "may happen"}`;
  return frame.kind === "past" ? "Depicted past" : frame.kind === "current" ? "Happening now" : "Imagined";
}
/** Narrative intent, independent of the renderer's upper/lower stage coordinates. */
export const zStoryTemporalFrame = z
  .object({
    /** Reuse within this story when returning to the same remembered interval. */
    id,
    kind: z.enum(["current", "past", "future", "imagined"]),
    future: zStoryFutureIntent.optional(),
    description: z.string().min(1).max(500),
    /** Explicit rewind/continuation boundary within this story; never inferred from recording order. */
    continuesSceneId: id.optional(),
    /** Exact story-local scene/frame boundaries, resolved to accepted captures on extraction. */
    chronology: z
      .object({
        before: z.array(z.object({ sceneId: id, frameId: id.optional() }).strict()).max(20),
        after: z.array(z.object({ sceneId: id, frameId: id.optional() }).strict()).max(20),
      })
      .strict()
      .optional(),
    /** Performed cast/representation IDs, not canonical person IDs or display names. */
    roleIds: z.array(id).max(60),
  })
  .strict();
export const zStoryTemporalFrames = z
  .array(zStoryTemporalFrame)
  .min(1)
  .max(4)
  .superRefine((frames, ctx) => {
    const ids = new Set<string>(),
      roles = new Set<string>();
    for (const [index, frame] of frames.entries()) {
      if (frame.chronology && frame.kind !== "past")
        ctx.addIssue({
          code: "custom",
          path: [index, "chronology"],
          message: "Historical ordering belongs to past frames, not predictions or imagined outcomes.",
        });
      if ((frame.kind === "future") !== !!frame.future)
        ctx.addIssue({
          code: "custom",
          path: [index, "future"],
          message: "Future frames require a basis and certainty; other frames must omit them.",
        });
      if (ids.has(frame.id))
        ctx.addIssue({ code: "custom", path: [index, "id"], message: "Duplicate temporal frame." });
      ids.add(frame.id);
      for (const role of frame.roleIds) {
        if (roles.has(role))
          ctx.addIssue({
            code: "custom",
            path: [index, "roleIds"],
            message: "One performed role cannot occupy two times; use a scene representation.",
          });
        roles.add(role);
      }
    }
    if (frames.filter((frame) => frame.kind === "current").length > 1)
      ctx.addIssue({ code: "custom", message: "A scene has at most one current frame." });
  });
export type StoryTemporalFrame = z.infer<typeof zStoryTemporalFrame>;
